// ============================================================
// SendCheckinTab.jsx - the dashboard trigger surface for
// checkinInstructionsService. Calls the exact same backend
// function the Property Ops Telegram bot calls - never
// duplicates the send logic itself.
// ============================================================

import { useState, useEffect, useCallback } from 'react';

const STATUS_LABELS = { verified: 'Code prêt', pending: 'Code en attente', none: 'Pas de code' };

function ReservationRow({ reservation, m, toast, onSent }) {
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [messageText, setMessageText] = useState('');

  const canSend = reservation.code_status === 'verified' && !reservation.already_sent;

  const openPreview = async () => {
    setLoadingPreview(true);
    try {
      const res = await m(`/admin/reservations/${reservation.id}/checkin-preview`);
      const data = await res.json();
      if (data.data?.ready) {
        setMessageText(data.data.message);
        setPreviewing(true);
      } else {
        const reasonText = {
          code_not_ready: 'Code pas encore prêt',
          no_hostex_channel: 'Aucun canal Hostex pour cette réservation',
        }[data.data?.reason] || data.data?.reason || 'Impossible de générer le message';
        toast(reasonText, 'err');
      }
    } catch (e) {
      toast('Erreur réseau', 'err');
    } finally {
      setLoadingPreview(false);
    }
  };

  const send = async () => {
    setSending(true);
    try {
      const res = await m(`/admin/reservations/${reservation.id}/send-checkin`, {
        method: 'POST',
        body: JSON.stringify({ message: messageText }),
      });
      const data = await res.json();
      if (data.data?.sent) {
        toast('Instructions envoyées', 'ok');
        setPreviewing(false);
        onSent();
      } else {
        const reasonText = {
          code_not_ready: 'Code pas encore prêt',
          already_sent: 'Déjà envoyé',
          no_hostex_channel: 'Aucun canal Hostex pour cette réservation',
          hostex_api_key_not_configured: "Clé API Hostex non configurée",
        }[data.data?.reason] || data.data?.reason || 'Échec';
        toast(reasonText, 'err');
      }
    } catch (e) {
      toast('Erreur réseau', 'err');
    } finally {
      setSending(false);
    }
  };

  if (previewing) {
    return (
      <div className="checkin-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 10 }}>
        <div className="checkin-guest">{reservation.guest_full_name}</div>
        <textarea
          className="fi"
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          rows={8}
          style={{ width: '100%', fontFamily: 'inherit', resize: 'vertical' }}
        />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-s" onClick={() => setPreviewing(false)} disabled={sending}>Annuler</button>
          <button className="checkin-send-btn" onClick={send} disabled={sending}>
            {sending ? '...' : '✅ Envoyer'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="checkin-row">
      <div>
        <div className="checkin-guest">{reservation.guest_full_name}</div>
        <div className="checkin-dates">
          {new Date(reservation.checkin_at).toLocaleDateString('fr-FR')} → {new Date(reservation.checkout_at).toLocaleDateString('fr-FR')}
        </div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <span className={`checkin-status-badge ${reservation.already_sent ? 'sent' : reservation.code_status}`}>
          {reservation.already_sent ? 'Envoyé' : STATUS_LABELS[reservation.code_status] || reservation.code_status}
        </span>
        <div>
          <button className="checkin-send-btn" onClick={openPreview} disabled={!canSend || loadingPreview} style={{ marginTop: 8 }}>
            {loadingPreview ? '...' : 'Aperçu / Envoyer'}
          </button>
        </div>
        {!canSend && !reservation.already_sent && (
          <div className="checkin-send-reason">
            {reservation.code_status !== 'verified' ? 'En attente de vérification du code' : ''}
          </div>
        )}
      </div>
    </div>
  );
}

export default function SendCheckinTab({ propertyId, m, toast }) {
  const [reservations, setReservations] = useState([]);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      m(`/admin/properties/${propertyId}/reservations`).then(r => r.json()),
      m(`/admin/properties/${propertyId}/checkin-log`).then(r => r.json()),
    ]).then(([resData, logData]) => {
      if (resData.success) setReservations(resData.data);
      if (logData.success) setLog(logData.data);
    }).finally(() => setLoading(false));
  }, [propertyId, m]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="empty"><span className="spin" /></div>;

  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Réservations récentes</div>
      {reservations.length === 0 ? (
        <div className="empty">
          <div className="empty-t">Aucune réservation</div>
        </div>
      ) : (
        reservations.map(r => (
          <ReservationRow key={r.id} reservation={r} m={m} toast={toast} onSent={load} />
        ))
      )}

      <hr style={{ border: 'none', borderTop: '0.5px solid var(--border)', margin: '20px 0' }} />

      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Historique d'envoi</div>
      {log.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--g2)' }}>Aucun envoi pour le moment.</div>
      ) : (
        log.map(entry => (
          <div key={entry.id} className="checkin-log-entry">
            <span>{entry.guest_full_name}</span>
            <span className={`checkin-log-status ${entry.payload_summary?.startsWith('SUCCESS') ? 'success' : 'failed'}`}>
              {entry.payload_summary?.startsWith('SUCCESS') ? '✓ Envoyé' : '✗ Échec'}
            </span>
            <span style={{ color: 'var(--g2)' }}>{new Date(entry.sent_at).toLocaleString('fr-FR')}</span>
          </div>
        ))
      )}
    </div>
  );
}
